import type { Row } from "./api";
import { fieldTargetId, type FieldIssue } from "./field-validation";
import {
  parseFilingInterview,
  deriveFilingInterview,
  filingInterviewValid,
  type FilingInterview,
} from "./filing-status";

export const filingStatusLabels = {
  SINGLE: "Single",
  MFJ: "Married Filing Jointly",
  MFS: "Married Filing Separately",
  HOH: "Head of Household",
  QSS: "Qualifying Surviving Spouse",
} as const;
export const filingLabels = {
  filingStatus: "Filing status",
  maritalSituation: "Marital situation on December 31, 2025",
  partnership: "Registered domestic partnership or civil union in 2025",
  spouseDeathYear: "Year the spouse died",
  recentSpouseDeath: "Spouse death during 2023–2025",
  communityIncome: "Community income or property requiring allocation",
  spouseName: "Spouse’s full legal name",
  spouseIdStatus: "Spouse’s Social Security number or ITIN availability",
  spouseTaxId: "Spouse’s Social Security number or ITIN",
  spouseResidency: "Spouse’s U.S. tax residency during 2025",
  spouseResidentElection:
    "Election to treat a nonresident spouse as a U.S. resident",
  jointReturnConsent: "Both spouses agree to file a joint return",
  spouseItemizes: "Spouse itemizes deductions on their separate return",
  livedWithSpouse: "Lived with the spouse at any time during 2025",
  spouseAbsentLastSixMonths:
    "Spouse lived outside the home for all of July–December 2025",
  separateReturn: "Filing a return separately from the spouse",
  paidMoreThanHalfHomeCosts:
    "Paid more than half the cost of keeping up the home",
  qualifyingPersonName: "Qualifying person’s full name",
  qualifyingPersonRelationship:
    "Qualifying person’s relationship to the taxpayer",
  qualifyingPersonResidence:
    "Time the qualifying person lived in the home during 2025",
  qualifyingPersonClaim: "How the qualifying person is treated as a dependent",
  jointReturnEligibleInDeathYear:
    "Eligible to file jointly in the year the spouse died",
} as const;
export type FilingField = keyof typeof filingLabels;
export function invalidSavedFilingInterview(raw: unknown): boolean {
  return Boolean(
    raw &&
    typeof raw === "object" &&
    Object.hasOwn(raw, "interview") &&
    !filingInterviewValid((raw as Row).interview),
  );
}
export const maritalSituationLabels: Record<string, string> = {
  NEVER_MARRIED: "Never married",
  DIVORCED_OR_SEPARATED: "Divorced or legally separated under a final decree",
  WIDOWED: "Widowed and not remarried",
  MARRIED: "Married, including living apart without a final decree",
  UNSURE: "Needs clarification",
};
export const hasMarriedTreatment = (v: FilingInterview) =>
  v.maritalSituation === "MARRIED" ||
  (v.maritalSituation === "WIDOWED" && v.spouseDeathYear === "2025");
export const isNonresidentHohPath = (v: FilingInterview) =>
  v.filingStatus === "HOH" &&
  hasMarriedTreatment(v) &&
  v.spouseResidency === "NONRESIDENT_OR_DUAL_STATUS" &&
  v.spouseResidentElection === "NO";
export function filingVisibleFields(
  v: FilingInterview,
  raw?: unknown,
): FilingField[] {
  const fields: FilingField[] = ["filingStatus"],
    status = v.filingStatus,
    history = v.maritalSituation;
  if (!status || status === "OTHER") return fields;
  fields.push("maritalSituation");
  const original = raw && typeof raw === "object" ? (raw as Row) : {};
  const details =
    original.state === "KNOWN"
      ? original.value
      : original.state
        ? undefined
        : original;
  const retainedCommunity =
    ["PRESENT", "CONFLICT"].includes(details?.items?.["SC-RDP-COMMUNITY"]) ||
    ["YES", "UNSURE"].includes(v.communityIncome ?? "");
  const spouseReturn = status === "MFJ" || status === "MFS";
  if (history === "WIDOWED" || status === "QSS") fields.push("spouseDeathYear");
  if (!spouseReturn && history && !["MARRIED", "UNSURE"].includes(history))
    fields.push("partnership");
  // Retain explicit historical concerns without asking new divorced filers a speculative death-screen question.
  if (
    history === "DIVORCED_OR_SEPARATED" &&
    ["YES", "UNSURE"].includes(v.recentSpouseDeath ?? "")
  )
    fields.push("recentSpouseDeath");
  if (spouseReturn) {
    fields.push("spouseName", "spouseIdStatus");
    if (v.spouseIdStatus === "HAS_ID") fields.push("spouseTaxId");
    fields.push("spouseResidency");
    if (status === "MFJ") {
      fields.push("jointReturnConsent");
      if (v.spouseResidency === "NONRESIDENT_OR_DUAL_STATUS")
        fields.push("spouseResidentElection");
    } else fields.push("spouseItemizes", "livedWithSpouse");
  }
  if (
    status === "MFS" ||
    (status === "HOH" && hasMarriedTreatment(v)) ||
    (!spouseReturn &&
      (["DIVORCED_OR_SEPARATED", "WIDOWED"].includes(history ?? "") ||
        v.partnership === "YES" ||
        retainedCommunity))
  )
    fields.push("communityIncome");
  if (status === "HOH" && hasMarriedTreatment(v)) {
    fields.push("spouseResidency");
    if (v.spouseResidency === "NONRESIDENT_OR_DUAL_STATUS")
      fields.push("spouseResidentElection");
    if (
      v.spouseResidency === "US_CITIZEN_OR_RESIDENT_ALL_YEAR" ||
      (v.spouseResidency === "NONRESIDENT_OR_DUAL_STATUS" &&
        v.spouseResidentElection === "YES")
    )
      fields.push("spouseAbsentLastSixMonths", "separateReturn");
  }
  if (status === "HOH" || status === "QSS") {
    if (status === "QSS") fields.push("jointReturnEligibleInDeathYear");
    fields.push(
      "paidMoreThanHalfHomeCosts",
      "qualifyingPersonName",
      "qualifyingPersonRelationship",
    );
    if (!(status === "HOH" && v.qualifyingPersonRelationship === "PARENT"))
      fields.push("qualifyingPersonResidence");
    fields.push("qualifyingPersonClaim");
  }
  return [...new Set(fields)];
}
const requiredReasons: Record<FilingField, string> = {
  filingStatus: "Choose one of the five filing statuses for this return.",
  maritalSituation:
    "Choose the taxpayer’s legal marital situation at the end of 2025. Living apart without a final decree does not, by itself, mean unmarried.",
  partnership:
    "Confirm whether the taxpayer had a legally registered partnership or civil union during 2025; this is different from sharing a home.",
  spouseDeathYear:
    "Choose the year the spouse died so the appropriate filing-status requirements can be reviewed.",
  recentSpouseDeath:
    "Review the previously recorded spouse-death information and confirm the answer.",
  communityIncome:
    "Confirm whether income or property must be allocated with a spouse, former spouse, or registered partner. Review the residence and ownership records; do not infer this from the current mailing address.",
  spouseName:
    "Enter the spouse’s full legal name as shown on their identity records.",
  spouseIdStatus:
    "Indicate whether the spouse has a Social Security number or ITIN; do not invent a number if it is unavailable.",
  spouseTaxId:
    "Enter the spouse’s nine-digit Social Security number or ITIN. Use synthetic information only in this workspace.",
  spouseResidency:
    "Choose the spouse’s U.S. tax residency for 2025; citizenship and where someone currently lives are not interchangeable.",
  spouseResidentElection:
    "Confirm whether an election to treat the nonresident spouse as a U.S. resident applies. Review the election records before answering.",
  jointReturnConsent:
    "Confirm whether both spouses agree to a joint return. A deceased or incapacitated spouse requires the applicable representative/signature review.",
  spouseItemizes:
    "Confirm whether the spouse itemizes on their separate return, because this affects the taxpayer’s deduction treatment.",
  livedWithSpouse:
    "Confirm whether the spouses lived together at any time in 2025; separate-return limitations can depend on this answer.",
  spouseAbsentLastSixMonths:
    "Confirm whether the spouse lived outside the home throughout July 1–December 31, 2025. A temporary absence does not count as living apart.",
  separateReturn:
    "Confirm that this return will be separate from the spouse’s return when using the married-but-living-apart household rules.",
  paidMoreThanHalfHomeCosts:
    "Compare the taxpayer’s payments with the total qualifying annual household costs. The taxpayer must pay more than half, not exactly half. For the parent route, review the parent’s main home.",
  qualifyingPersonName:
    "Enter the name of the person whose household and dependency circumstances support this filing status.",
  qualifyingPersonRelationship:
    "Choose the person’s actual relationship to the taxpayer; being claimed as a dependent does not by itself establish a qualifying person.",
  qualifyingPersonResidence:
    "Choose the time this person lived in the home, accounting for permitted temporary absences. Select exception review for birth, death, placement, or other special circumstances.",
  qualifyingPersonClaim:
    "Identify the dependency treatment using the Dependents records, including any custody release or exception needing review.",
  jointReturnEligibleInDeathYear:
    "Confirm eligibility to file jointly in the year of death. The taxpayer need not actually have filed a joint return that year.",
};
export function filingStatusFieldIssues(
  raw: unknown,
  rootId: string,
  selected: Row = {},
  previousRaw: unknown = raw,
): FieldIssue[] {
  const v = parseFilingInterview(raw),
    visible = filingVisibleFields(v, raw);
  const issue = (
    field: FilingField,
    kind: "required" | "invalid",
    reason: string,
  ): FieldIssue => ({
    id: fieldTargetId(rootId, [field]) + "-" + kind,
    targetId: fieldTargetId(rootId, [field]),
    path: [field],
    sectionId: "filing-status",
    sectionLabel: "Filing Status",
    fieldLabel: filingLabels[field],
    kind,
    reason,
  });
  if (invalidSavedFilingInterview(raw))
    return [
      {
        ...issue(
          "filingStatus",
          "invalid",
          "Previously saved filing-status information cannot be edited safely in this form. Ask an administrator to review it; the original entry has been preserved.",
        ),
        targetId: rootId + "-saved",
        fieldLabel: "Saved filing-status information",
      },
    ];
  const result: FieldIssue[] = [];
  for (const field of visible) {
    const item = v[field];
    if (
      !item ||
      (typeof item === "string" && !item.trim()) ||
      (field === "filingStatus" && item === "OTHER")
    )
      result.push(issue(field, "required", requiredReasons[field]));
    else if (
      item === "UNSURE" &&
      !["spouseName", "spouseTaxId", "qualifyingPersonName"].includes(field)
    )
      result.push(
        issue(
          field,
          "invalid",
          "This answer still needs clarification. " + requiredReasons[field],
        ),
      );
  }
  const invalid = (field: FilingField, reason: string) => {
    if (visible.includes(field)) result.push(issue(field, "invalid", reason));
  };
  const status = v.filingStatus,
    history = v.maritalSituation;
  if (
    visible.includes("spouseTaxId") &&
    v.spouseTaxId &&
    (!/^\d{9}$/.test(v.spouseTaxId.replace(/[\s-]/g, "")) ||
      /^0{9}$/.test(v.spouseTaxId.replace(/[\s-]/g, "")))
  )
    invalid(
      "spouseTaxId",
      "Enter nine digits for the spouse’s Social Security number or ITIN, with optional spaces or hyphens. An all-zero number cannot be used.",
    );
  if (status === "SINGLE" && history === "MARRIED")
    invalid(
      "maritalSituation",
      "The taxpayer is recorded as married, but Single is selected. Review the legal marital status or choose another filing status.",
    );
  if (
    status === "SINGLE" &&
    history === "WIDOWED" &&
    v.spouseDeathYear === "2025"
  )
    invalid(
      "spouseDeathYear",
      "A spouse’s death during 2025 generally requires married filing-status treatment for 2025. Review the selected filing status.",
    );
  if (
    ["MFJ", "MFS"].includes(status ?? "") &&
    (["NEVER_MARRIED", "DIVORCED_OR_SEPARATED"].includes(history ?? "") ||
      (history === "WIDOWED" &&
        ["2024", "2023", "EARLIER"].includes(v.spouseDeathYear ?? "")))
  )
    invalid(
      "maritalSituation",
      "The recorded marital history does not establish married treatment for 2025. Review the year-end relationship information or select a filing status for an unmarried taxpayer.",
    );
  if (status === "MFJ" && v.jointReturnConsent === "NO")
    invalid(
      "jointReturnConsent",
      "A joint return requires both spouses’ agreement or the applicable authorized representative process. Review consent or consider Married Filing Separately.",
    );
  if (status === "QSS") {
    if (history && !["WIDOWED", "UNSURE"].includes(history))
      invalid(
        "maritalSituation",
        "Qualifying Surviving Spouse requires the relevant spouse-death history and no remarriage by the end of 2025. Review the relationship history.",
      );
    if (
      v.spouseDeathYear &&
      !["2023", "2024", "UNSURE"].includes(v.spouseDeathYear)
    )
      invalid(
        "spouseDeathYear",
        "For a 2025 return, Qualifying Surviving Spouse requires the spouse to have died in 2023 or 2024. A 2025 death uses the year-of-death filing-status rules.",
      );
    if (v.jointReturnEligibleInDeathYear === "NO")
      invalid(
        "jointReturnEligibleInDeathYear",
        "This filing status requires eligibility to file jointly in the year of death, even if a joint return was not actually filed. Review the earlier-year circumstances.",
      );
    if (
      v.qualifyingPersonRelationship &&
      !["CHILD", "UNSURE"].includes(v.qualifyingPersonRelationship)
    )
      invalid(
        "qualifyingPersonRelationship",
        "Qualifying Surviving Spouse requires a son, daughter, adopted child, or stepchild. A foster child, parent, or other relative does not meet this relationship requirement.",
      );
    if (
      ["MORE_THAN_HALF", "HALF_OR_LESS"].includes(
        v.qualifyingPersonResidence ?? "",
      )
    )
      invalid(
        "qualifyingPersonResidence",
        "Qualifying Surviving Spouse generally requires the child’s main home for the entire year. Review permitted absences and special circumstances; use exception review when applicable.",
      );
    if (v.qualifyingPersonClaim === "CUSTODIAL_PARENT_RELEASE")
      invalid(
        "qualifyingPersonClaim",
        "A custodial-parent release alone does not satisfy the Qualifying Surviving Spouse dependency requirement. Review the child’s eligibility and any specifically permitted exception.",
      );
  }
  if (status === "HOH" || status === "QSS") {
    if (v.paidMoreThanHalfHomeCosts === "NO")
      invalid(
        "paidMoreThanHalfHomeCosts",
        "This filing status requires the taxpayer to pay more than half the qualifying cost of maintaining the home. Review household costs or select another filing status.",
      );
    if (v.qualifyingPersonClaim === "NOT_CLAIMED")
      invalid(
        "qualifyingPersonClaim",
        "Review why this person is not being claimed. A qualifying-child or permitted dependency exception may matter; do not assume every nondependent qualifies.",
      );
  }
  if (status === "HOH") {
    if (v.qualifyingPersonRelationship === "UNRELATED")
      invalid(
        "qualifyingPersonRelationship",
        "An unrelated person does not qualify the taxpayer for Head of Household merely because the person lives in the household or is a dependent.",
      );
    if (
      v.qualifyingPersonResidence === "HALF_OR_LESS" &&
      v.qualifyingPersonRelationship !== "PARENT"
    )
      invalid(
        "qualifyingPersonResidence",
        "This route generally requires the qualifying person’s main home for more than half the year. Review permitted absences or select exception review for special circumstances.",
      );
    if (hasMarriedTreatment(v) && !isNonresidentHohPath(v)) {
      if (v.spouseAbsentLastSixMonths === "NO")
        invalid(
          "spouseAbsentLastSixMonths",
          "The married-but-living-apart route requires the spouse to be absent from the home for all of July–December. Review the dates or another applicable filing-status route.",
        );
      if (v.separateReturn === "NO")
        invalid(
          "separateReturn",
          "The married-but-living-apart Head of Household route requires a separate return, not a joint return with the spouse.",
        );
      if (
        visible.includes("spouseAbsentLastSixMonths") &&
        v.qualifyingPersonRelationship &&
        !["CHILD", "FOSTER_CHILD", "UNSURE"].includes(
          v.qualifyingPersonRelationship,
        )
      )
        invalid(
          "qualifyingPersonRelationship",
          "The married-but-living-apart route requires a child, stepchild, or foster child. Supporting a parent alone does not meet that route.",
        );
    }
    if (
      v.qualifyingPersonClaim === "CUSTODIAL_PARENT_RELEASE" &&
      v.qualifyingPersonRelationship &&
      !["CHILD", "FOSTER_CHILD", "UNSURE"].includes(
        v.qualifyingPersonRelationship,
      )
    )
      invalid(
        "qualifyingPersonClaim",
        "A custodial-parent release applies to the child’s dependency treatment, not to a parent or another relative. Review this selection.",
      );
  }
  for (const conflict of deriveFilingInterview(v, selected, previousRaw)
    .conflicts) {
    if (conflict.target === "filingStatus") continue;
    const preferred: FilingField = [
      "SC-MARRIAGE",
      "taxpayer.marital_status_year_end",
    ].includes(conflict.target)
      ? "maritalSituation"
      : conflict.target === "SC-RDP-COMMUNITY"
        ? "communityIncome"
        : conflict.target === "SC-SPOUSE-DEATH" && history === "WIDOWED"
          ? "spouseDeathYear"
          : "filingStatus";
    result.push({
      ...issue(
        visible.includes(preferred) ? preferred : "filingStatus",
        "invalid",
        conflict.message.replace(/^Filing Status — [^:]+: /, ""),
      ),
      id: rootId + "-review-" + conflict.target,
    });
  }
  return result;
}
export function filingStatusSummary(raw: unknown): string {
  if (invalidSavedFilingInterview(raw))
    return "Saved filing-status information needs administrator review";
  const v = parseFilingInterview(raw),
    source = raw && typeof raw === "object" ? (raw as Row) : {};
  const items = source.state === "KNOWN" ? source.value?.items : source.items;
  const needsReview =
    source.state === "CONFLICT" ||
    Object.values(items ?? {}).some((item) => item === "CONFLICT") ||
    (v.filingStatus === "SINGLE" &&
      Object.values(items ?? {}).some((item) => item === "PRESENT"));
  return [
    v.filingStatus && v.filingStatus in filingStatusLabels
      ? filingStatusLabels[v.filingStatus as keyof typeof filingStatusLabels]
      : v.filingStatus === "OTHER"
        ? "Another filing status — review needed: choose the exact status"
        : "Filing status not selected",
    v.maritalSituation ? maritalSituationLabels[v.maritalSituation] : undefined,
    v.partnership === "YES" || v.communityIncome === "YES"
      ? "Partnership or shared-income review required"
      : needsReview
        ? "Additional filing-status review required"
        : undefined,
  ]
    .filter(Boolean)
    .join(" · ");
}
export function filingHouseholdSummary(selected: Row): string {
  const raw = selected.answers?.["Q-009"],
    v = raw?.state === "KNOWN" ? raw.value : raw?.state ? undefined : raw;
  if (!v || typeof v !== "object")
    return "Complete Dependents so household information can be used here. You do not need to answer those questions again.";
  if (
    v.claimable_status === true ||
    v.dependent_count > 0 ||
    v.qualifying_person_count > 0
  )
    return "The Dependents section contains household information for filing-status review. Those answers are retained when the filing status changes.";
  if (
    v.claimable_status === false &&
    v.dependent_count === 0 &&
    v.qualifying_person_count === 0
  )
    return "Dependents records no dependents or qualifying people and says no one else can claim the taxpayer. Any unresolved support or custody matters still require review.";
  return "Some household details are still missing. Complete them in Dependents; they will be reused here.";
}
